import React, { useEffect, useRef } from 'react'
import useSearch from '../../../context/search/useSearch';
import getRoute from '../../../../scripts/utils/getRoute'

interface ISearchInputComponent {
  dataSet: DOMStringMap
}

const SearchInput: React.FC<ISearchInputComponent> = ({dataSet}): JSX.Element => {

  const { langSearchInputPlaceholder, langSearchInputValue } = dataSet

  const {quickSearchTerm, inputEventHandlers, setQuickSearchTerm, isSearching, quickSearchResultsSuggestions} = useSearch()

  const inputRef = useRef<HTMLInputElement>(null)

  const setBodyClass = (show) => {
    show ? document.body.classList.add('search-open', 'modal-open') : document.body.classList.remove('search-open', 'modal-open');
  }

  const handleKeyDown = (e) => {
    inputEventHandlers.handleSearchInputKeyPress(e)
  }

  const handleOnChange = (e) => {
    inputEventHandlers.handleSearchInputChange(e)
  }

  const handleFocus = (e) => {
    if (!dataSet.position.includes('404')) {
      setBodyClass(true);
    }
    // Trigger the search handler on focus
    inputEventHandlers.handleSearchInputChange(e);
    // Also trigger the focus handler for trending/popular data
    inputEventHandlers.handleSearchInputFocus();
  }

  const handleBlur = () => {
    document.body.addEventListener('click', e => {
      const target = e.target as HTMLElement
      // If we click and we're not within the search results or search wrapper then remove the body class if the search modal is open
      !target.closest('[data-search-content]') && !target.closest('[data-search-wrapper]') && document.body.classList.contains('search-open') ? setBodyClass(false) : null;
    })
  }

  useEffect(() => {
    // Set the quick search term to the default value grabbed from dataset on first render only
    setQuickSearchTerm(langSearchInputValue)
    window.addEventListener('blubolt:close-search', () => {
      setQuickSearchTerm(langSearchInputValue);
      handleOnChange({
        target: {
          value: ''
        }
      })
    })
  }, []);

  // Inline autocomplete (ghost complete inside input using selection)
  useEffect(() => {
    const el = inputRef.current
    if (!el) return

    const term = (quickSearchTerm || '').toString()
    // Find top suggestion that starts with the term
    const top = (quickSearchResultsSuggestions || []).find(s => {
      const q = (s?.value || '').toString()
      return q.length > term.length && q.toLowerCase().startsWith(term.toLowerCase())
    })

    if (top && term.length) {
      const suggestion = (top.value || '').toString()
      // Mutate DOM value to show completion, but keep state as the user's term
      el.value = suggestion
      try {
        el.setSelectionRange(term.length, suggestion.length)
      } catch (e) {
        // ignore selection issues (e.g., unsupported input types)
      }
    } else {
      // Re-sync DOM to state when no completion
      el.value = term
    }
  }, [quickSearchTerm, quickSearchResultsSuggestions])

  return (
    <>
      <div className={'search__input-container'}>
        <input
          tabIndex={0}
          className={'search__input'}
          id={'search__input'}
          type="text"
          placeholder={langSearchInputPlaceholder}
          ref={inputRef}
          value={quickSearchTerm}
          onBlur={handleBlur}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          onChange={handleOnChange}
        />
        {isSearching && <div className={'search__is-searching'}></div>}
        {quickSearchTerm && quickSearchTerm.length && !isSearching &&
          <button
            onClick={(e) => {
              setQuickSearchTerm('')
              handleOnChange(e)
              setBodyClass(false)
            }}
            className={'search__clear'}
          ><svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 34 35"><path stroke="#000" strokeMiterlimit="10" strokeWidth="2" d="M25.456 9.23 8.486 25.938M8.486 9.23l16.97 16.708"/></svg></button>}
        <button
          type="button"
          className="search__btn"
          aria-label="Search"
          data-graphene-search-btn=""
          onClick={() => { window.location.href = `${getRoute()}search?q=${quickSearchTerm}`; }}
        >
          <svg width="6" height="9" viewBox="0 0 6 9" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M5 1L1 4.5L5 8" stroke="white" strokeLinecap="square" strokeWidth="0.5px"></path>
          </svg>
        </button>
      </div>
    </>
  )
};

export default SearchInput